import * as React from 'react';
import styled from 'styled-components';
import { selectDoesUserHaveRbacPermission } from 'core/userContext/selectors';
import { useSelector } from 'react-redux';

import { PROJECT_PERMISSIONS } from 'utils/permissions';

import { ActionMenu } from 'ui/actionMenu';
import { SecondaryButton } from 'ui/button';
import { colors, spacing } from 'ui/theme';

import { WorkflowType } from 'modules/workflows/enums';
import { WorkflowId, WorkflowIdData, WorkflowStatusData } from 'modules/workflowIndex/types';
import {
  Archive,
  Delete,
  MakeCopy,
  Overflow,
  Undo,
  Stop,
  StartFilled,
  MoveToFolder,
} from 'ui/icons';
import { ActionMenuOption, AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';

const { useMemo } = React;

const displayText = {
  enableOption: 'Turn on',
  deleteOption: 'Delete',
  disableOption: 'Turn off',
  copyOption: 'Make a copy',
  archiveOption: 'Archive',
  unarchiveOption: 'Unarchive',
  moveOption: 'Move to folder',
};

const StyledButton = styled(SecondaryButton)`
  padding: 0 ${spacing.half};
`;

type WorkflowRowOverflowMenuProps = {
  workflow: WorkflowIdData & WorkflowStatusData;
  onArchiveOptionClick: (workflowId: WorkflowId) => void;
  onUnarchiveOptionClick: (workflowId: WorkflowId) => void;
  onCopyOptionClick: (workflowId: WorkflowId) => void;
  onDeleteOptionClick: (workflowId: WorkflowId) => void;
  onEnableOptionClick: (workflowId: WorkflowId) => void;
  onDisableOptionClick: (workflowId: WorkflowId) => void;
  onMoveOptionClick: (workflowId: WorkflowId) => void;
  canDraftMessaging?: boolean;
  canManageMessaging?: boolean;
};

const getActions = ({
  workflow,
  onArchiveOptionClick,
  onCopyOptionClick,
  onDeleteOptionClick,
  onUnarchiveOptionClick,
  onEnableOptionClick,
  onDisableOptionClick,
  onMoveOptionClick,
  canDraftMessaging,
  canManageMessaging,
}: WorkflowRowOverflowMenuProps): ActionMenuOption[] =>
  [
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <MoveToFolder height={16} />
          <span>{displayText.moveOption}</span>
        </>
      ),
      id: 'move-to-folder',
      onClick: () => onMoveOptionClick(workflow.id),
      enabled: !workflow.isArchived,
    },
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <StartFilled height={16} />
          <span>{displayText.enableOption}</span>
        </>
      ),
      id: 'enable',
      onClick: () => onEnableOptionClick(workflow.id),
      enabled:
        !workflow.isArchived && !workflow.enabled && workflow.workflowType !== WorkflowType.DRAFT,
    },
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <Stop height={16} />
          <span>{displayText.disableOption}</span>
        </>
      ),
      id: 'disable',
      onClick: () => onDisableOptionClick(workflow.id),
      enabled: !workflow.isArchived && workflow.enabled,
    },
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <MakeCopy height={16} />
          <span>{displayText.copyOption}</span>
        </>
      ),
      id: 'make-a-copy',
      onClick: () => onCopyOptionClick(workflow.id),
      enabled: canDraftMessaging && !workflow.isArchived,
    },
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <Archive height={16} />
          <span>{displayText.archiveOption}</span>
        </>
      ),
      id: 'archive',
      onClick: () => onArchiveOptionClick(workflow.id),
      enabled:
        canManageMessaging && !workflow.isArchived && workflow.workflowType !== WorkflowType.DRAFT,
    },
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <Delete height={16} />
          <span>{displayText.deleteOption}</span>
        </>
      ),
      id: 'delete',
      onClick: () => onDeleteOptionClick(workflow.id),
      enabled: workflow.workflowType && workflow.workflowType === WorkflowType.DRAFT,
    },
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <Undo height={16} />
          <span>{displayText.unarchiveOption}</span>
        </>
      ),
      id: 'unarchive',
      onClick: () => onUnarchiveOptionClick(workflow.id),
      enabled: workflow.isArchived,
    },
  ].filter(({ enabled }) => enabled);

export const WorkflowRowOverflowMenu = ({
  workflow,
  onArchiveOptionClick,
  onCopyOptionClick,
  onDeleteOptionClick,
  onUnarchiveOptionClick,
  onEnableOptionClick,
  onDisableOptionClick,
  onMoveOptionClick,
}: WorkflowRowOverflowMenuProps) => {
  const canDraftMessaging = useSelector(selectDoesUserHaveRbacPermission)(
    PROJECT_PERMISSIONS.DraftMessaging,
  );
  const canManageMessaging = useSelector(selectDoesUserHaveRbacPermission)(
    PROJECT_PERMISSIONS.ManageMessaging,
  );

  const options = useMemo(
    () =>
      getActions({
        workflow,
        onArchiveOptionClick,
        onCopyOptionClick,
        onDeleteOptionClick,
        onUnarchiveOptionClick,
        onEnableOptionClick,
        onDisableOptionClick,
        onMoveOptionClick,
        canDraftMessaging,
        canManageMessaging,
      }),
    [
      workflow,
      onArchiveOptionClick,
      onCopyOptionClick,
      onDeleteOptionClick,
      onUnarchiveOptionClick,
      onEnableOptionClick,
      onDisableOptionClick,
    ],
  );

  return (
    <ActionMenu
      align={MENU_ALIGNMENTS.RIGHT}
      options={options}
      id={`workflow-overflow-menu-${workflow.id}`}
    >
      <StyledButton onClick={() => null}>
        <Overflow color={colors.grey90} />
      </StyledButton>
    </ActionMenu>
  );
};
